import { create } from "zustand";
import {
  acceptInvite,
  createCategory,
  createCommunity,
  createChannel,
  createCommunityMessage,
  deleteCategory,
  deleteChannel,
  deleteCommunityMessage,
  getCommunity,
  listCommunityMessages,
  listMembers,
  listMyCommunities,
  listNotifPrefs,
  listReadState,
  markChannelRead,
  setNotifPref,
  toggleCommunityReaction,
  updateCategory,
  updateChannel,
  updateCommunityMessage,
  type CategoryResponse,
  type ChannelOverride,
  type ChannelResponse,
  type CommunityPageResponse,
  type CommunityResponse,
  type CreateChannelInput,
  type CreateCommunityInput,
  type MemberListItem,
  type MessageResponse,
  type NotifLevel,
  type NotifPrefEntry,
  type RoleResponse,
} from "@slumbr/core";

interface CommunitiesState {
  /** Sidebar rail — communities I'm a member of. */
  mine: CommunityResponse[];
  loadingMine: boolean;

  /** Cache of full community pages, keyed by community id. */
  cache: Record<string, CommunityPageResponse>;
  /** Per-channel message slices. */
  messages: Record<string, MessageResponse[]>;
  /** Member list per community. */
  members: Record<string, MemberListItem[]>;

  /** Per-channel last-read pointer, keyed by channelId. Loaded
   *  lazily when a community is opened; updated optimistically when the
   *  user views a channel. */
  readState: Record<
    string,
    { lastReadMessageId: string; lastReadAt: number }
  >;

  /** Per-community notification prefs keyed by scope (channelId,
   *  categoryId, or `*`). */
  notifPrefs: Record<string, NotifPrefEntry[]>;

  activeCommunityId: string | null;
  activeChannelId: string | null;
  error: string | null;

  fetchMine: () => Promise<void>;
  fetchCommunity: (id: string) => Promise<CommunityPageResponse | null>;
  setActiveCommunity: (id: string | null) => void;
  setActiveChannel: (id: string | null) => void;

  fetchMessages: (id: string, channelId: string) => Promise<void>;
  fetchMembers: (id: string) => Promise<void>;
  fetchReadState: (id: string) => Promise<void>;
  fetchNotifPrefs: (id: string) => Promise<void>;
  updateNotifPref: (
    id: string,
    scope: string,
    level: NotifLevel,
    mutedUntil?: number | null
  ) => Promise<void>;

  sendMessage: (
    id: string,
    channelId: string,
    content: unknown,
    options?: { replyToId?: string }
  ) => Promise<MessageResponse>;
  editMessage: (
    id: string,
    channelId: string,
    messageId: string,
    content: unknown
  ) => Promise<MessageResponse>;
  removeMessage: (
    id: string,
    channelId: string,
    messageId: string
  ) => Promise<void>;
  toggleReaction: (
    id: string,
    channelId: string,
    messageId: string,
    emoji: string
  ) => Promise<MessageResponse>;
  markRead: (
    id: string,
    channelId: string,
    messageId: string,
    messageTs: number
  ) => Promise<void>;

  createCommunity: (input: CreateCommunityInput) => Promise<CommunityResponse>;
  createChannelInCommunity: (
    id: string,
    input: CreateChannelInput
  ) => Promise<ChannelResponse>;
  updateChannelInCommunity: (
    id: string,
    channelId: string,
    input: Partial<CreateChannelInput> & { overrides?: ChannelOverride[] }
  ) => Promise<ChannelResponse>;
  deleteChannelInCommunity: (id: string, channelId: string) => Promise<void>;
  createCategoryInCommunity: (
    id: string,
    input: { name: string; position?: number }
  ) => Promise<CategoryResponse>;
  renameCategoryInCommunity: (
    id: string,
    categoryId: string,
    name: string
  ) => Promise<CategoryResponse>;
  deleteCategoryInCommunity: (id: string, categoryId: string) => Promise<void>;
  acceptInviteCode: (code: string) => Promise<CommunityResponse>;

  /** Push a single message into the local cache (realtime fanout uses this). */
  ingestMessage: (channelId: string, message: MessageResponse) => void;
  /** Replace / delete a message in place. */
  patchMessage: (
    channelId: string,
    messageId: string,
    patch: Partial<MessageResponse> | null
  ) => void;
}

const MAX_CACHED_MESSAGES = 200;

export const useCommunitiesStore = create<CommunitiesState>((set, get) => ({
  mine: [],
  loadingMine: false,
  cache: {},
  messages: {},
  members: {},
  readState: {},
  notifPrefs: {},
  activeCommunityId: null,
  activeChannelId: null,
  error: null,

  fetchMine: async () => {
    set({ loadingMine: true, error: null });
    try {
      const res = await listMyCommunities();
      set({ mine: res.items, loadingMine: false });
    } catch (err) {
      set({
        loadingMine: false,
        error: err instanceof Error ? err.message : "Failed to load communities",
      });
    }
  },

  fetchCommunity: async (id) => {
    try {
      const res = await getCommunity(id);
      set((s) => ({
        cache: { ...s.cache, [id]: res },
      }));
      return res;
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : "Failed to load community",
      });
      return null;
    }
  },

  setActiveCommunity: (id) => set({ activeCommunityId: id }),
  setActiveChannel: (id) => set({ activeChannelId: id }),

  fetchMessages: async (id, channelId) => {
    try {
      const res = await listCommunityMessages(id, channelId, { limit: 50 });
      set((s) => ({
        messages: { ...s.messages, [channelId]: res.items },
      }));
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : "Failed to load messages",
      });
    }
  },

  fetchMembers: async (id) => {
    try {
      const res = await listMembers(id, { limit: 100 });
      set((s) => ({
        members: { ...s.members, [id]: res.items },
      }));
    } catch (err) {
      console.warn("Failed to load members", err);
    }
  },

  sendMessage: async (id, channelId, content, options) => {
    const message = await createCommunityMessage(id, channelId, {
      content,
      ...(options?.replyToId ? { replyToId: options.replyToId } : {}),
    });
    get().ingestMessage(channelId, message);
    return message;
  },

  editMessage: async (id, channelId, messageId, content) => {
    const updated = await updateCommunityMessage(id, channelId, messageId, {
      content,
    });
    get().patchMessage(channelId, messageId, updated);
    return updated;
  },

  removeMessage: async (id, channelId, messageId) => {
    await deleteCommunityMessage(id, channelId, messageId);
    get().patchMessage(channelId, messageId, null);
  },

  toggleReaction: async (id, channelId, messageId, emoji) => {
    const updated = await toggleCommunityReaction(
      id,
      channelId,
      messageId,
      emoji
    );
    get().patchMessage(channelId, messageId, updated);
    return updated;
  },

  fetchReadState: async (id) => {
    try {
      const res = await listReadState(id);
      set((s) => {
        const next = { ...s.readState };
        for (const row of res.items) {
          next[row.channelId] = {
            lastReadMessageId: row.lastReadMessageId,
            lastReadAt: row.lastReadAt,
          };
        }
        return { readState: next };
      });
    } catch (err) {
      console.warn("Failed to load read state", err);
    }
  },

  fetchNotifPrefs: async (id) => {
    try {
      const res = await listNotifPrefs(id);
      set((s) => ({
        notifPrefs: { ...s.notifPrefs, [id]: res.items },
      }));
    } catch (err) {
      console.warn("Failed to load notif prefs", err);
    }
  },

  updateNotifPref: async (id, scope, level, mutedUntil) => {
    // Optimistic update of the local cache.
    set((s) => {
      const current = s.notifPrefs[id] ?? [];
      const existing = current.find((entry) => entry.scope === scope);
      const next: NotifPrefEntry = {
        scope,
        level,
        mutedUntil: mutedUntil ?? null,
        updatedAt: Date.now(),
      };
      const merged = existing
        ? current.map((entry) => (entry.scope === scope ? next : entry))
        : [...current, next];
      return { notifPrefs: { ...s.notifPrefs, [id]: merged } };
    });
    try {
      await setNotifPref(id, {
        scope,
        level,
        mutedUntil: mutedUntil ?? null,
      });
    } catch (err) {
      console.warn("Failed to set notif pref", err);
    }
  },

  markRead: async (id, channelId, messageId, messageTs) => {
    // Optimistic — flip the local pointer first so badges clear
    // immediately. Server reconciles on resolve.
    set((s) => ({
      readState: {
        ...s.readState,
        [channelId]: { lastReadMessageId: messageId, lastReadAt: messageTs },
      },
    }));
    try {
      await markChannelRead(id, channelId, messageId, messageTs);
    } catch (err) {
      console.warn("Failed to mark channel read", err);
    }
  },

  createCommunity: async (input) => {
    const community = await createCommunity(input);
    set((s) => ({ mine: [community, ...s.mine] }));
    return community;
  },

  createChannelInCommunity: async (id, input) => {
    const channel = await createChannel(id, input);
    set((s) => {
      const existing = s.cache[id];
      if (!existing) return s;
      return {
        cache: {
          ...s.cache,
          [id]: {
            ...existing,
            channels: [...existing.channels, channel].sort(
              (a, b) => a.position - b.position || a.id.localeCompare(b.id)
            ),
          },
        },
      };
    });
    return channel;
  },

  updateChannelInCommunity: async (id, channelId, input) => {
    const page = get().cache[id];
    if (!page) {
      return updateChannel(id, channelId, input);
    }

    // Save previous state for rollback
    const backup = JSON.parse(JSON.stringify(page));

    // Optimistically update local cache
    set((s) => {
      const existing = s.cache[id];
      if (!existing) return s;
      return {
        cache: {
          ...s.cache,
          [id]: {
            ...existing,
            channels: existing.channels
              .map((channel) =>
                channel.id === channelId ? { ...channel, ...input } : channel
              )
              .sort((a, b) => a.position - b.position || a.id.localeCompare(b.id)),
          },
        },
      };
    });

    try {
      const updated = await updateChannel(id, channelId, input);
      set((s) => {
        const existing = s.cache[id];
        if (!existing) return s;
        return {
          cache: {
            ...s.cache,
            [id]: {
              ...existing,
              channels: existing.channels
                .map((channel) => (channel.id === channelId ? updated : channel))
                .sort((a, b) => a.position - b.position || a.id.localeCompare(b.id)),
            },
          },
        };
      });
      return updated;
    } catch (err) {
      // Rollback on failure
      set((s) => ({
        cache: {
          ...s.cache,
          [id]: backup,
        },
      }));
      throw err;
    }
  },

  deleteChannelInCommunity: async (id, channelId) => {
    await deleteChannel(id, channelId);
    set((s) => {
      const existing = s.cache[id];
      if (!existing) return s;
      return {
        cache: {
          ...s.cache,
          [id]: {
            ...existing,
            channels: existing.channels.filter((channel) => channel.id !== channelId),
          },
        },
      };
    });
  },

  createCategoryInCommunity: async (id, input) => {
    const category = await createCategory(id, input);
    set((s) => {
      const existing = s.cache[id];
      if (!existing) return s;
      return {
        cache: {
          ...s.cache,
          [id]: {
            ...existing,
            categories: [...existing.categories, category].sort(
              (a, b) => a.position - b.position || a.id.localeCompare(b.id)
            ),
          },
        },
      };
    });
    return category;
  },

  renameCategoryInCommunity: async (id, categoryId, name) => {
    const updated = await updateCategory(id, categoryId, { name });
    set((s) => {
      const existing = s.cache[id];
      if (!existing) return s;
      return {
        cache: {
          ...s.cache,
          [id]: {
            ...existing,
            categories: existing.categories.map((category) =>
              category.id === categoryId ? updated : category
            ),
          },
        },
      };
    });
    return updated;
  },

  deleteCategoryInCommunity: async (id, categoryId) => {
    await deleteCategory(id, categoryId);
    set((s) => {
      const existing = s.cache[id];
      if (!existing) return s;
      return {
        cache: {
          ...s.cache,
          [id]: {
            ...existing,
            categories: existing.categories.filter(
              (category) => category.id !== categoryId
            ),
            // Unparent any channels that lived under this category — the
            // server-side delete leaves them in place but null-categories
            // them logically; mirror that in the local cache.
            channels: existing.channels.map((channel) =>
              channel.categoryId === categoryId
                ? { ...channel, categoryId: null }
                : channel
            ),
          },
        },
      };
    });
  },

  acceptInviteCode: async (code) => {
    const res = await acceptInvite(code);
    set((s) => {
      const existing = s.mine.some((community) => community.id === res.community.id);
      return existing
        ? s
        : { mine: [res.community, ...s.mine] };
    });
    return res.community;
  },

  ingestMessage: (channelId, message) => {
    set((s) => {
      const list = s.messages[channelId] ?? [];
      if (list.some((m) => m.id === message.id)) return s;
      const next = [...list, message].slice(-MAX_CACHED_MESSAGES);
      return { messages: { ...s.messages, [channelId]: next } };
    });
  },

  patchMessage: (channelId, messageId, patch) => {
    set((s) => {
      const list = s.messages[channelId];
      if (!list) return s;
      const next =
        patch === null
          ? list.filter((m) => m.id !== messageId)
          : list.map((m) => (m.id === messageId ? { ...m, ...patch } : m));
      return { messages: { ...s.messages, [channelId]: next } };
    });
  },
}));

/** Convenience selector — pick a community's full page response (or null). */
export function selectCommunityPage(
  state: CommunitiesState,
  id: string | null
): CommunityPageResponse | null {
  if (!id) return null;
  return state.cache[id] ?? null;
}

/** Pick a channel by id from the cached page. */
export function findChannel(
  page: CommunityPageResponse | null,
  channelId: string | null
): ChannelResponse | null {
  if (!page || !channelId) return null;
  return page.channels.find((channel) => channel.id === channelId) ?? null;
}

export function findCategory(
  page: CommunityPageResponse | null,
  categoryId: string | null
): CategoryResponse | null {
  if (!page || !categoryId) return null;
  return page.categories.find((category) => category.id === categoryId) ?? null;
}

export function findRole(
  page: CommunityPageResponse | null,
  roleId: string | null
): RoleResponse | null {
  if (!page || !roleId) return null;
  return page.roles.find((role) => role.id === roleId) ?? null;
}
